/**
 * <JobSelectionCard>
 *
 * The "Job Selection" card that used to live inline at the top of
 * PreConForm.js. Extracted into a shared component so the equipment
 * checklist forms (Check-In, Check-Out, Pre-Trip) can reuse the exact
 * same UX and data source without diverging.
 *
 * Contract (kept intentionally narrow so the caller owns state):
 * - `value`             — currently-selected job.id (or '')
 * - `onChange(jobId)`   — fires when user picks a job. Caller looks it
 *                         up in `availableJobs` and does its own field
 *                         auto-fill (this keeps caller-specific form
 *                         shapes out of the shared component).
 * - `availableJobs`     — list from GET /api/jobs/for-field-reports
 * - `loadingJobs`       — bool, shows spinner while true
 * - `selectedJob`       — the resolved job object (or null) for the
 *                         summary box; caller passes this in so we
 *                         don't have to duplicate the find() call.
 * - `disabled`          — bool, freezes the select (view-only or editing)
 * - `required`          — bool, shows the red asterisk + warning border
 *                         when empty (defaults true — every consumer
 *                         wants this so far)
 * - `myJobIds`           — optional array of job.id assigned to the
 *                         signed-in user today (from
 *                         GET /api/time-blocks/assigned-jobs, same
 *                         source Time Tracker v2 uses). When non-empty,
 *                         renders a one-tap "Your Jobs Today" row above
 *                         the search dropdown. Omit entirely for callers
 *                         that don't want this (no behavior change).
 * - `summaryVariant`    — 'simple' (default, unchanged amber bullet
 *                         list) | 'detailed' (branded <JobInfoPanel>
 *                         matching the Field Report PDF look — opt-in
 *                         per caller so CSE/PreCon/Soft Dig/Locate keep
 *                         their existing look unless explicitly changed)
 */
import React, { useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, AlertCircle } from 'lucide-react';
import { filterJobsByQuery } from '../lib/jobSearch';
import JobInfoPanel from './JobInfoPanel';

export default function JobSelectionCard({
  value,
  onChange,
  availableJobs,
  loadingJobs,
  selectedJob,
  disabled = false,
  required = true,
  myJobIds = [],
  testIdPrefix = 'job-selection',
  summaryVariant = 'simple',
}) {
  const empty = !value;
  // 2026-08-26 — search box for the job picker, matching Time
  // Tracker's job search exactly (same filterJobsByQuery helper).
  // Resets whenever the dropdown closes so a stale search doesn't
  // leak into the next time it's opened.
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const filteredJobs = filterJobsByQuery(availableJobs, jobSearchQuery);
  const myJobIdSet = new Set(myJobIds);
  const myJobs = availableJobs.filter(j => myJobIdSet.has(j.id));
  return (
    <Card
      className={required && empty && !disabled ? 'border-amber-500 border-2' : ''}
      data-testid={`${testIdPrefix}-card`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">★</span>
          Job Selection {required && <span className="text-red-500">*</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loadingJobs ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading available jobs...
          </div>
        ) : availableJobs.length === 0 ? (
          <div className="text-center py-4">
            <AlertCircle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
            <p className="text-slate-600">No active jobs available</p>
            <p className="text-sm text-slate-400">You may not have any jobs assigned</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* "Your Jobs Today" — same pattern as Time Tracker v2:
                surface the signed-in user's own assigned jobs as one-tap
                picks, full search stays available right below for
                anything else (helping on an unassigned job, historical
                reference, etc). */}
            {myJobs.length > 0 && !disabled && (
              <div className="space-y-1.5" data-testid={`${testIdPrefix}-my-jobs-today`}>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Your Jobs Today</Label>
                <div className="flex flex-wrap gap-2">
                  {myJobs.map(job => (
                    <button
                      key={job.id}
                      type="button"
                      onClick={() => onChange(job.id)}
                      data-testid={`${testIdPrefix}-my-job-${job.job_number}`}
                      className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                        value === job.id
                          ? 'border-amber-500 bg-amber-50 text-amber-900'
                          : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50/60'
                      }`}
                    >
                      <span className="font-medium">{job.job_number}</span>
                      <span className="text-slate-500"> · {job.job_title || job.client_name || 'Untitled'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>Select Job Number {required && <span className="text-red-500">*</span>}</Label>
              <Select
                value={value || ''}
                onValueChange={onChange}
                disabled={disabled}
                onOpenChange={(open) => { if (!open) setJobSearchQuery(''); }}
              >
                <SelectTrigger
                  className={required && empty ? 'border-amber-500' : ''}
                  data-testid={`${testIdPrefix}-trigger`}
                >
                  <SelectValue placeholder="Select a job..." />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-4rem)] w-[var(--radix-select-trigger-width)]">
                  {/* Search input — sticky at the top so it stays visible
                      while scrolling the list. Stops propagation on key
                      events so Radix Select's built-in keyboard nav
                      doesn't hijack the typing (same pattern as Time
                      Tracker's job search). */}
                  <div className="sticky top-0 bg-white z-10 p-2 border-b border-slate-200">
                    <Input
                      autoFocus
                      data-testid={`${testIdPrefix}-search-input`}
                      placeholder="Search by customer, address, job # or keyword…"
                      value={jobSearchQuery}
                      onChange={(e) => setJobSearchQuery(e.target.value)}
                      onKeyDown={(e) => { e.stopPropagation(); }}
                      className="h-9 text-sm"
                    />
                  </div>
                  {filteredJobs.length === 0 ? (
                    <div className="py-4 px-3 text-sm text-slate-500 text-center" data-testid={`${testIdPrefix}-no-matches`}>
                      No jobs match &ldquo;{jobSearchQuery}&rdquo;. Try fewer or different keywords.
                    </div>
                  ) : (
                    filteredJobs.map(job => {
                      const label = `${job.job_number} - ${job.job_title || job.client_name || 'Untitled'}`;
                      return (
                        <SelectItem
                          key={job.id}
                          value={job.id}
                          textValue={label}
                          data-testid={`${testIdPrefix}-option-${job.job_number}`}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{label}</span>
                            <span className="text-xs text-slate-500">{job.service_address}</span>
                          </div>
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedJob && (
              summaryVariant === 'detailed' ? (
                <JobInfoPanel job={selectedJob} testIdPrefix={testIdPrefix} />
              ) : (
                <div
                  className="bg-amber-50 p-3 rounded-lg text-sm space-y-1"
                  data-testid={`${testIdPrefix}-summary`}
                >
                  <p><strong>Job:</strong> {selectedJob.job_number}</p>
                  {selectedJob.job_title && <p><strong>Description:</strong> {selectedJob.job_title}</p>}
                  <p><strong>Address:</strong> {selectedJob.service_address}</p>
                  <p><strong>Date:</strong> {selectedJob.date}</p>
                  <p><strong>Team:</strong> {selectedJob.assigned_team_names?.join(', ') || 'Not assigned'}</p>
                </div>
              )
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
